import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * Elvaire updates:
 * - External variant videos can be inserted into the mixed media gallery.
 * - External videos autoplay muted when sufficiently visible.
 * - Videos pause when they leave the viewport / active slide.
 * - Native browser video controls are hidden.
 * - A custom centered play/pause button controls playback.
 * - Zoom indexes still map correctly when videos are inserted before images.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    this.#setupElvaireVideos();
  }

  #controller = new AbortController();
  #videoObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#videoObserver?.disconnect();
    this.#videoObserver = null;

    this.#pauseAllElvaireVideos();
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery
   * with the newly server-rendered gallery for the selected variant.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery] Event promise rejected:', error);
        }
      });
  };

  /**
   * The zoom dialog contains only Shopify-native media.
   * External metafield videos are not put into the zoom dialog.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    const slideshow = this.slideshow;

    if (!slideshow) return;

    const zoomIndex = Number(event.detail.index);
    const mixedSlides = slideshow.slides;

    if (!mixedSlides?.length) {
      slideshow.select(zoomIndex, undefined, { animate: false });
      return;
    }

    const matchingSlide = mixedSlides.find(
      (slide) => Number(slide.getAttribute('data-elvaire-zoom-index')) === zoomIndex
    );

    if (!matchingSlide) {
      slideshow.select(zoomIndex, undefined, { animate: false });
      return;
    }

    const mixedIndex = mixedSlides.indexOf(matchingSlide);

    slideshow.select(mixedIndex, undefined, { animate: false });
  };

  /**
   * Setup autoplay + custom controls for external variant videos.
   */
  #setupElvaireVideos() {
    const players = [...this.querySelectorAll('[data-elvaire-video-player]')];

    if (!players.length) return;

    const { signal } = this.#controller;

    for (const player of players) {
      if (!(player instanceof HTMLElement)) continue;

      const video = player.querySelector('[data-elvaire-video]');
      const toggle = player.querySelector('[data-elvaire-video-toggle]');

      if (!(video instanceof HTMLVideoElement)) continue;

      video.muted = true;
      video.playsInline = true;

      if (toggle instanceof HTMLButtonElement) {
        toggle.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (video.paused) {
              this.#playElvaireVideo(video);
            } else {
              video.pause();
            }
          },
          { signal }
        );
      }

      video.addEventListener(
        'play',
        () => {
          player.setAttribute('data-playing', 'true');

          if (toggle instanceof HTMLButtonElement) {
            toggle.setAttribute('aria-label', 'Pause video');
          }
        },
        { signal }
      );

      video.addEventListener(
        'pause',
        () => {
          player.setAttribute('data-playing', 'false');

          if (toggle instanceof HTMLButtonElement) {
            toggle.setAttribute('aria-label', 'Play video');
          }
        },
        { signal }
      );

      video.addEventListener(
        'ended',
        () => {
          player.setAttribute('data-playing', 'false');

          if (toggle instanceof HTMLButtonElement) {
            toggle.setAttribute('aria-label', 'Play video');
          }
        },
        { signal }
      );

      video.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (video.paused) {
            this.#playElvaireVideo(video);
          } else {
            video.pause();
          }
        },
        { signal }
      );
    }

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const player = entry.target;

          if (!(player instanceof HTMLElement)) continue;

          const video = player.querySelector('[data-elvaire-video]');

          if (!(video instanceof HTMLVideoElement)) continue;

          const slide = player.closest('slideshow-slide');

          const slideIsActive =
            !slide ||
            slide.getAttribute('aria-hidden') === 'false' ||
            slide.hasAttribute('reveal');

          if (entry.isIntersecting && entry.intersectionRatio >= 0.65 && slideIsActive) {
            this.#playElvaireVideo(video);
          } else {
            video.pause();
          }
        }
      },
      {
        threshold: [0, 0.25, 0.5, 0.65, 0.8, 1],
      }
    );

    for (const player of players) {
      if (player instanceof HTMLElement) {
        this.#videoObserver.observe(player);
      }
    }

    const slideshowElement = this.querySelector('slideshow-component');

    if (slideshowElement instanceof HTMLElement) {
      const mutationObserver = new MutationObserver(() => {
        this.#syncElvaireVideoPlayback();
      });

      const slides = slideshowElement.querySelectorAll('slideshow-slide');

      for (const slide of slides) {
        mutationObserver.observe(slide, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'hidden', 'reveal'],
        });
      }

      signal.addEventListener(
        'abort',
        () => mutationObserver.disconnect(),
        { once: true }
      );
    }

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.#pauseAllElvaireVideos();
        } else {
          this.#syncElvaireVideoPlayback();
        }
      },
      { signal }
    );
  }

  /**
   * Play one video and pause the others.
   *
   * @param {HTMLVideoElement} video
   */
  async #playElvaireVideo(video) {
    for (const other of this.querySelectorAll('[data-elvaire-video]')) {
      if (other instanceof HTMLVideoElement && other !== video) {
        other.pause();
      }
    }

    video.muted = true;

    try {
      await video.play();
    } catch (error) {
      if (error?.name !== 'AbortError' && error?.name !== 'NotAllowedError') {
        console.warn('[media-gallery] Could not play external variant video:', error);
      }
    }
  }

  /**
   * Re-check which video should currently play.
   */
  #syncElvaireVideoPlayback() {
    const players = [...this.querySelectorAll('[data-elvaire-video-player]')];

    for (const player of players) {
      if (!(player instanceof HTMLElement)) continue;

      const video = player.querySelector('[data-elvaire-video]');

      if (!(video instanceof HTMLVideoElement)) continue;

      const rect = player.getBoundingClientRect();

      const visibleHeight =
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

      const visibilityRatio =
        rect.height > 0
          ? Math.max(0, Math.min(1, visibleHeight / rect.height))
          : 0;

      const slide = player.closest('slideshow-slide');

      const slideIsActive =
        !slide ||
        slide.getAttribute('aria-hidden') === 'false' ||
        slide.hasAttribute('reveal');

      if (visibilityRatio >= 0.65 && slideIsActive && !document.hidden) {
        this.#playElvaireVideo(video);
      } else {
        video.pause();
      }
    }
  }

  /**
   * Pause every Elvaire external video in this gallery.
   */
  #pauseAllElvaireVideos() {
    for (const video of this.querySelectorAll('[data-elvaire-video]')) {
      if (video instanceof HTMLVideoElement) {
        video.pause();
      }
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}